import { useLocation, useNavigate, useParams } from 'react-router';
import { useCallback, useMemo } from 'react';
import queryString from 'query-string';

export const useSearchParams = <
  T extends { [x: string]: string | number | null | undefined } = any,
>(): { query: Partial<T>; replace: (replacedQuery: T) => void } => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const query = useMemo(() => {
    const parsedQuery = queryString.parse(location.search);

    const combinedQuery: Partial<T> = {
      ...(parsedQuery as Partial<T>),
      ...(params as Partial<T>),
    };

    return combinedQuery;
  }, [params, location.search]);

  const replace = useCallback(
    (replacedQuery: T) => {
      const searchParams = new URLSearchParams(window.location.search);

      Object.entries(replacedQuery).forEach(([key, value]) => {
        const hasParam = searchParams.has(key);

        if (hasParam && value !== null && value !== '') {
          searchParams.set(key, String(value));
        } else if (hasParam && (value === null || value === '')) {
          searchParams.delete(key);
        } else if (!hasParam && value !== null && value !== '') {
          searchParams.set(key, String(value));
        }
      });
      navigate(
        {
          pathname: window.location.pathname,
          search: `?${searchParams}`,
        },
        { replace: true },
      );
    },
    [navigate],
  );

  return useMemo(() => {
    return {
      query,
      replace,
    };
  }, [query, replace]);
};
